/**
 * Pure presentation component for the drawer's Contract-tab body when a
 * loaded CONTRACT extraction is being displayed.
 *
 * Composition order (top-to-bottom):
 * 1. `ContractDrawerStatusStrip` — the SAVED-mode chrome (green badge +
 *    "Saved {date}" + Edit button). SE-4767 inlines the Save and
 *    Discard buttons into the strip's `extraActions` slot so all three
 *    action affordances render on a single horizontal row instead of
 *    stacking vertically. The strip handles its own visibility gating
 *    (hidden when no extraction or when archived).
 * 2. `ContractDrawerEditModeBanner` — shown while the user is editing a
 *    previously SAVED extraction.
 * 3. `ContractExtractionView` — shared atom that renders the
 *    collapsible category list plus the "+ Add Document" affordance via
 *    the `showAddDocumentAction` flag. SE-4767: `showDiscardButton` is
 *    pinned `false` here because the Discard button is now slotted into
 *    the action row above. Using the shared atom keeps this surface
 *    aligned with the mobile full-page route (ContractScreen) and the
 *    Player Page Financials tab.
 *
 * SE-4605: This body is now mounted INSIDE the Contract tab slot of
 * the drawer-level `ContractDrawerTabs`; it therefore no longer passes
 * `showTabs` to the shared atom (doing so would nest a second tab
 * strip inside the outer one). The sibling Transfer Agreement
 * extraction is owned + fetched by `ContractDrawerContainer` and
 * rendered directly under the Transfer Agreement tab slot.
 *
 * The SAVED-view "View Player Page" affordance previously lived in
 * `ContractDrawerSavedHeader`; its Profile-link counterpart now lives
 * in the drawer's top-level `ContractDrawerHeader` (variant="drawer")
 * chrome which is rendered by the parent shell.
 * @module features/contracts/components/ContractDrawerExtractionBody/ContractDrawerExtractionBodyView
 */

import { Trash2 } from "lucide-react-native";
import React, { memo } from "react";

import type { ContractExtraction } from "@/features/contracts/types";
import ContractDrawerEditModeBanner from "@/features/contracts/components/molecules/ContractDrawerEditModeBanner";
import ContractDrawerStatusStrip from "@/features/contracts/components/molecules/ContractDrawerStatusStrip";
import ContractExtractionView from "@/features/contracts/components/organisms/ContractExtractionView";
import SaveExtractionSection from "@/features/contracts/components/organisms/SaveExtractionSection";

import { Button } from "@/components/atoms/Button";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";

/** Props for {@link ContractDrawerExtractionBodyView}. */
interface Props {
  /** Loaded extraction the body renders against. */
  readonly extraction: ContractExtraction;
  /** Player UUID from the drawer state, possibly null while transitioning. */
  readonly playerId: string | null;
  /** Document type (drawer state). */
  readonly documentType: string;
  /**
   * Whether the drawer is in read-only mode (non-OWNER, archived, or
   * SAVED). Suppresses the Edit affordance in the status strip, the
   * "+ Add Document" CTA, and forwards through to the data atom so
   * its inline edit/delete affordances are hidden.
   */
  readonly isReadOnly: boolean;
  /**
   * Whether the extraction is in `SAVED` mode AND the user is not
   * currently editing. Drives both the status-strip visibility logic
   * and the `readOnly` flag on the data atom.
   */
  readonly isSavedViewMode: boolean;
  /**
   * Whether the user is actively editing a previously SAVED
   * extraction. Drives the edit-mode banner render and forces the
   * data atom out of read-only mode regardless of SAVED state.
   */
  readonly isEditMode: boolean;
  /** Whether the Save button should be offered (`PENDING_REVIEW`). */
  readonly canSave: boolean;
  /**
   * The player the drawer is saving against. Falls back to the extraction's own
   * player when the drawer state's is null mid-transition, so downstream
   * save/refetch calls always receive a non-empty id.
   */
  readonly resolvedPlayerId: string;
  /** Whether the inline data atom is read-only. */
  readonly dataAtomReadOnly: boolean;
  /** Whether the row-inlined Discard button renders. */
  readonly showDiscardButton: boolean;
  /** Whether Save renders in the StatusStrip's extraActions slot. */
  readonly showSaveButtonInStrip: boolean;
  /** Whether Save renders in the EditModeBanner's saveAction slot. */
  readonly showSaveButtonInBanner: boolean;
  /** Whether the Add Document action renders. */
  readonly showAddDocumentAction: boolean;
  /** Invoked when the user taps the Discard button. */
  readonly onOpenDeleteDialog: () => void;
  /** Invoked when the user taps Edit in the status strip. */
  readonly onEnterEditMode: () => void;
  /** Invoked when the user taps Cancel in the edit-mode banner. */
  readonly onRequestCancel: () => void;
  /** Invoked when the user taps the "+ Add Document" CTA in SAVED view. */
  readonly onAddDocumentPress: () => void;
}

/**
 * The compact Save affordance.
 *
 * SE-4768 renders the SAME component in two slots — the StatusStrip's
 * `extraActions` outside edit mode, the EditModeBanner's `saveAction` inside it
 * — so the Save → confirmation-dialog → toast wiring is identical on both
 * surfaces rather than reimplemented per surface.
 * @param props - View props.
 * @returns The compact save section.
 */
const renderCompactSave = (props: Props): React.ReactElement => (
  <SaveExtractionSection
    compact
    extractionId={props.extraction.id}
    extractedData={props.extraction.extractedData ?? {}}
    playerId={props.resolvedPlayerId}
    documentType={props.documentType}
  />
);

/**
 * The StatusStrip's trailing actions: Discard, Save, or neither.
 * @param props - View props.
 * @returns The actions, or null when neither renders.
 */
const renderExtraActions = (props: Props): React.ReactElement | null =>
  props.showDiscardButton || props.showSaveButtonInStrip ? (
    <>
      {props.showDiscardButton ? (
        <Button
          testID="discard-extraction-btn"
          label="Discard"
          tone="danger"
          variant="outline"
          size="sm"
          icon={Trash2}
          onPress={props.onOpenDeleteDialog}
        />
      ) : null}
      {props.showSaveButtonInStrip ? renderCompactSave(props) : null}
    </>
  ) : null;

/**
 * The edit-mode banner's Save slot.
 *
 * Suppressed entirely when there is nothing to commit, so the banner stays
 * Cancel-only until the first per-row mutation flips the extraction to
 * PENDING_REVIEW.
 * @param props - View props.
 * @returns The save action, or null.
 */
const renderEditBannerSaveAction = (props: Props): React.ReactElement | null =>
  props.showSaveButtonInBanner ? renderCompactSave(props) : null;

/**
 * Renders the drawer Contract-tab body for a loaded extraction.
 *
 * Takes the whole props object rather than destructuring: it hands props
 * straight to the three render helpers above, so a destructure here would only
 * have to be undone at every call site.
 * @param props - View props
 * @returns The rendered body
 */
const ContractDrawerExtractionBodyView = (props: Props) => (
  <ScrollView className="flex-1 p-4">
    <Stack className="gap-4">
      <ContractDrawerStatusStrip
        savedAt={props.extraction.savedAt ?? null}
        hasExtraction
        isArchived={false}
        isReadOnly={props.isReadOnly || props.isEditMode}
        onEditPress={props.onEnterEditMode}
        extraActions={renderExtraActions(props)}
      />
      <ContractDrawerEditModeBanner
        isVisible={props.isEditMode}
        onCancelPress={props.onRequestCancel}
        saveAction={renderEditBannerSaveAction(props)}
      />
      <ContractExtractionView
        // Force a full remount — and therefore a reset of the
        // per-category collapse state — when the drawer's player
        // changes. Without this, the `useState` mount-time seed
        // inside `ContractExtractionViewContainer` persists the
        // previous player's collapsed categories onto the next
        // player's list (CodeRabbit Minor on the container).
        key={props.resolvedPlayerId}
        extraction={props.extraction}
        playerId={props.resolvedPlayerId}
        documentType={props.documentType}
        readOnly={props.dataAtomReadOnly}
        // Discard is now slotted into the StatusStrip action row
        // (SE-4767); pin the inline button off so it doesn't render
        // twice.
        showDiscardButton={false}
        showAddDocumentAction={props.showAddDocumentAction}
        onOpenDeleteDialog={props.onOpenDeleteDialog}
        onAddDocumentPress={props.onAddDocumentPress}
      />
    </Stack>
  </ScrollView>
);

ContractDrawerExtractionBodyView.displayName =
  "ContractDrawerExtractionBodyView";

export default memo(ContractDrawerExtractionBodyView);
